// i18n keys of the chart of accounts' change log (COA-10): the History drawer on a client's chart
// (src/components/Bookkeeping/AccountHistory.tsx) and Admin → Chart template's "Change history and
// integrity" section (src/components/Admin/CoaChangeLog.tsx), en / zh / th.
// ⛔ Fill all three: an empty value falls back or renders blank. ⛔ Counted keys are _one / _other —
// `_plural` is dead in i18next 23.
// ⛔ ONLY THE SCREENS' OWN WORDS. Account codes and names, the values a change moved from and to,
// who made it and the source it came through are data, shown exactly as recorded in every language.
export const en: Record<string, string> = {
  coal_history: 'History',
  coal_history_tip: 'Who changed this account, when, and what it was before',
  coal_history_title: 'Change history — {{code}} {{name}}',
  coal_history_lead: 'Every change to this account, oldest first: who made it, when, what it was before and what it became.',
  coal_history_close: 'Close',
  coal_history_loading: 'Loading…',
  coal_history_empty: 'Nothing has been recorded for this account.',
  coal_history_denied: 'This login cannot see the chart\'s change history: it names the firm\'s staff who made each change.',
  coal_history_failed: 'The history could not be loaded: {{error}}',
  coal_act_baseline: 'Recorded as it stood when the change log began',
  coal_act_baseline_late: 'Found with no history and recorded late',
  coal_act_create: 'Created',
  coal_act_update: 'Changed',
  coal_act_remove: 'Removed',
  coal_act_backfill: 'Filled in automatically',
  coal_act_copy: 'Copied',
  coal_act_override: 'Firm judgement recorded',
  coal_by: 'by {{who}}',
  coal_via: 'through {{source}}',
  coal_empty_value: '(blank)',
  coal_snapshot: 'Account as recorded:',
  coal_field_id: 'Account id',
  coal_field_client_id: 'Client',
  coal_field_template_id: 'Copied from template account',
  coal_field_code: 'Code',
  coal_field_name_th: 'Thai name',
  coal_field_name_en: 'English name',
  coal_field_class: 'Class',
  coal_field_control: 'Control (AR / AP)',
  coal_field_parent_id: 'Parent account',
  coal_field_dbd_line_item_id: 'DBD line item',
  coal_field_tax_flags: 'Tax flags',
  coal_field_active: 'In use',
  coal_field_sort_order: 'Order',
  coal_field_created_by: 'Created by',
  coal_field_created_at: 'Created at',
  coal_field_deleted: 'Removed',
  coal_field_cit_deductible: 'CIT deductible',
  coal_field_cit_limit_note: 'CIT limit note',
  coal_field_capital_nature: 'Capital in nature',
  coal_field_cf_section: 'Cash flow section',
  coal_field_fs_section: 'Working-account section',
  coal_field_account_type: 'Account type',
  coal_field_account_group: 'Group',
  coal_field_description: 'Description',
  coal_field_control_role: 'Control role',
  coal_field_subgroup: 'Subgroup',
  coal_field_system_notes: 'System / notes',
  // Batch 8 R1: an account's role (chart_of_accounts.role, tracked since R1) — the value is shown as recorded.
  coal_field_role: 'Role',
  coal_admin_title: 'Change history and integrity of the charts of accounts',
  coal_admin_lead: 'Every write to a chart of accounts, each client\'s and the house template new clients are copied from, leaves a row here. The two checks below compare the log with the charts themselves.',
  coal_tile_rows: 'Rows in the log',
  coal_tile_baselines: 'Accounts in the starting record',
  coal_tile_late: 'Recorded late',
  coal_tile_late_hint: 'Accounts found at a start-up with no history at all: written by something other than the chart recorder.',
  coal_tile_replay: 'Do not match their log',
  coal_tile_replay_hint: 'Accounts whose change log, replayed, does not give the account as it is now: changed outside the recorder. Checked just now over {{n}} accounts.',
  coal_untracked: 'Columns the log does not follow: {{cols}}',
  coal_mismatch_lead: 'The first accounts that do not match:',
  coal_why_no_history: 'no history',
  coal_why_removed: 'deleted in the log, still in the chart',
  coal_why_differs: 'differs in {{cols}}',
  coal_why_gap: 'changed outside the log between two logged changes, in {{cols}}',
  coal_why_missing: 'in the log, gone from the chart with no logged deletion',
  coal_why_recreated: 'deleted outside the log, then created again under the same id',
  coal_filter_client: 'Client',
  coal_all_clients: 'All clients',
  coal_filter_scope: 'Scope',
  coal_all_scopes: 'All scopes',
  coal_scope_client_chart: 'Client charts',
  coal_scope_house_template: 'House template',
  coal_scope_master_firm_layer: 'Firm layer on the master template',
  coal_scope_asset_category: 'Asset categories',
  coal_house: 'House template',
  coal_col_when: 'When',
  coal_col_client: 'Client',
  coal_col_account: 'Account',
  coal_col_change: 'Change',
  coal_col_who: 'Who',
  coal_col_source: 'Through',
  coal_rows_one: '{{count}} row',
  coal_rows_other: '{{count}} rows',
  coal_none: 'Nothing recorded yet.',
  coal_load_failed: 'Could not load the change log: {{error}}',
};

export const zh: Record<string, string> = {
  coal_history: '历史',
  coal_history_tip: '谁在什么时候改了这个科目，改之前是什么',
  coal_history_title: '变更历史 — {{code}} {{name}}',
  coal_history_lead: '这个科目的每一次变更，按时间先后：谁改的、什么时候、改之前是什么、改成了什么。',
  coal_history_close: '关闭',
  coal_history_loading: '加载中…',
  coal_history_empty: '这个科目还没有任何记录。',
  coal_history_denied: '此登录不能查看科目表的变更历史：其中有事务所经手员工的姓名。',
  coal_history_failed: '无法加载变更历史：{{error}}',
  coal_act_baseline: '变更日志开始时的原样',
  coal_act_baseline_late: '发现时没有任何历史，补记',
  coal_act_create: '新建',
  coal_act_update: '修改',
  coal_act_remove: '删除',
  coal_act_backfill: '系统自动补填',
  coal_act_copy: '复制',
  coal_act_override: '记录事务所判断',
  coal_by: '经手：{{who}}',
  coal_via: '来源：{{source}}',
  coal_empty_value: '（空）',
  coal_snapshot: '记录时的科目：',
  coal_field_id: '科目 id',
  coal_field_client_id: '客户',
  coal_field_template_id: '复制自模板科目',
  coal_field_code: '科目码',
  coal_field_name_th: '泰文名称',
  coal_field_name_en: '英文名称',
  coal_field_class: '类别',
  coal_field_control: '控制科目（应收 / 应付）',
  coal_field_parent_id: '上级科目',
  coal_field_dbd_line_item_id: 'DBD 列报项目',
  coal_field_tax_flags: '税务标记',
  coal_field_active: '启用',
  coal_field_sort_order: '排序',
  coal_field_created_by: '创建人',
  coal_field_created_at: '创建时间',
  coal_field_deleted: '已删除',
  coal_field_cit_deductible: '企业所得税可扣除',
  coal_field_cit_limit_note: '企业所得税限额说明',
  coal_field_capital_nature: '资本性支出',
  coal_field_cf_section: '现金流量表分类',
  coal_field_fs_section: 'บัญชีทำการ 分类',
  coal_field_account_type: '科目类型',
  coal_field_account_group: '分组',
  coal_field_description: '描述',
  coal_field_control_role: '控制角色',
  coal_field_subgroup: '分组(Subgroup)',
  coal_field_system_notes: '系统 / 备注',
  coal_field_role: '用途',
  coal_admin_title: '科目表的变更历史与完整性',
  coal_admin_lead: '对科目表的每一次写入，无论是各客户的科目表，还是新客户复制所用的现行模板，都会在这里留下一行。下面两项检查把日志与科目表本身逐一比对。',
  coal_tile_rows: '日志行数',
  coal_tile_baselines: '起始记录中的科目',
  coal_tile_late: '补记',
  coal_tile_late_hint: '启动时发现完全没有历史的科目：是记录器以外的东西写进去的。',
  coal_tile_replay: '与日志不符',
  coal_tile_replay_hint: '按变更日志重放后得不到当前样子的科目：在记录器之外被改过。刚刚对 {{n}} 个科目检查过。',
  coal_untracked: '日志未跟踪的列：{{cols}}',
  coal_mismatch_lead: '最先发现不符的科目：',
  coal_why_no_history: '没有历史',
  coal_why_removed: '日志里已删除，科目表里仍在',
  coal_why_differs: '不同的列：{{cols}}',
  coal_why_gap: '两次记录之间在日志之外被改过的列：{{cols}}',
  coal_why_missing: '日志里仍在，科目表里已没有，且没有删除记录',
  coal_why_recreated: '在日志之外被删除，之后又以同一 id 重新建立',
  coal_filter_client: '客户',
  coal_all_clients: '全部客户',
  coal_filter_scope: '范围',
  coal_all_scopes: '全部范围',
  coal_scope_client_chart: '客户科目表',
  coal_scope_house_template: '现行模板',
  coal_scope_master_firm_layer: '主模板上的事务所层',
  coal_scope_asset_category: '资产类别',
  coal_house: '现行模板',
  coal_col_when: '时间',
  coal_col_client: '客户',
  coal_col_account: '科目',
  coal_col_change: '变更',
  coal_col_who: '经手人',
  coal_col_source: '来源',
  coal_rows_one: '{{count}} 行',
  coal_rows_other: '{{count}} 行',
  coal_none: '还没有任何记录。',
  coal_load_failed: '无法加载变更日志：{{error}}',
};

export const th: Record<string, string> = {
  coal_history: 'ประวัติ',
  coal_history_tip: 'ใครเปลี่ยนบัญชีนี้ เมื่อไร และก่อนเปลี่ยนเป็นอย่างไร',
  coal_history_title: 'ประวัติการเปลี่ยนแปลง — {{code}} {{name}}',
  coal_history_lead: 'การเปลี่ยนแปลงทุกครั้งของบัญชีนี้ เรียงจากเก่าไปใหม่: ใครเปลี่ยน เมื่อไร ก่อนเปลี่ยนเป็นอะไร และเปลี่ยนเป็นอะไร',
  coal_history_close: 'ปิด',
  coal_history_loading: 'กำลังโหลด…',
  coal_history_empty: 'ยังไม่มีการเปลี่ยนแปลงใดของบัญชีนี้',
  coal_history_denied: 'ผู้ใช้นี้ดูประวัติการเปลี่ยนแปลงผังบัญชีไม่ได้ เพราะประวัติระบุชื่อพนักงานของสำนักงานที่เป็นผู้เปลี่ยน',
  coal_history_failed: 'โหลดประวัติไม่ได้: {{error}}',
  coal_act_baseline: 'สภาพ ณ วันที่เริ่มเก็บประวัติ',
  coal_act_baseline_late: 'พบโดยไม่มีประวัติ จึงเก็บย้อนหลัง',
  coal_act_create: 'สร้าง',
  coal_act_update: 'เปลี่ยน',
  coal_act_remove: 'ลบ',
  coal_act_backfill: 'ระบบเติมให้อัตโนมัติ',
  coal_act_copy: 'คัดลอก',
  coal_act_override: 'ความเห็นของสำนักงาน',
  coal_by: 'โดย {{who}}',
  coal_via: 'ผ่าน {{source}}',
  coal_empty_value: '(ว่าง)',
  coal_snapshot: 'บัญชีตามที่เก็บไว้:',
  coal_field_id: 'รหัสภายในของบัญชี',
  coal_field_client_id: 'ลูกค้า',
  coal_field_template_id: 'คัดลอกจากบัญชีแม่แบบ',
  coal_field_code: 'รหัสบัญชี',
  coal_field_name_th: 'ชื่อภาษาไทย',
  coal_field_name_en: 'ชื่อภาษาอังกฤษ',
  coal_field_class: 'หมวด',
  coal_field_control: 'บัญชีคุม (ลูกหนี้ / เจ้าหนี้)',
  coal_field_parent_id: 'บัญชีแม่',
  coal_field_dbd_line_item_id: 'รายการย่อ DBD',
  coal_field_tax_flags: 'เครื่องหมายภาษี',
  coal_field_active: 'ใช้งาน',
  coal_field_sort_order: 'ลำดับ',
  coal_field_created_by: 'ผู้สร้าง',
  coal_field_created_at: 'เวลาที่สร้าง',
  coal_field_deleted: 'ลบแล้ว',
  coal_field_cit_deductible: 'หักเป็นรายจ่ายทางภาษีได้',
  coal_field_cit_limit_note: 'หมายเหตุเพดานรายจ่าย',
  coal_field_capital_nature: 'รายจ่ายลักษณะทุน',
  coal_field_cf_section: 'หมวดในงบกระแสเงินสด',
  coal_field_fs_section: 'ตำแหน่งในบัญชีทำการ',
  coal_field_account_type: 'ประเภทบัญชี',
  coal_field_account_group: 'กลุ่ม',
  coal_field_description: 'คำอธิบาย',
  coal_field_control_role: 'บทบาทบัญชีคุม',
  coal_field_subgroup: 'กลุ่มย่อย (Subgroup)',
  coal_field_system_notes: 'ระบบ / หมายเหตุ',
  coal_field_role: 'หน้าที่ของบัญชี',
  coal_admin_title: 'ประวัติการเปลี่ยนแปลงและความครบถ้วนของผังบัญชี',
  coal_admin_lead: 'การเขียนผังบัญชีทุกครั้ง ทั้งของลูกค้าแต่ละราย และแม่แบบที่ใช้คัดลอกให้ลูกค้าใหม่ จะเหลือแถวไว้ที่นี่ การตรวจสองข้อด้านล่างเทียบประวัติกับผังบัญชีจริง',
  coal_tile_rows: 'จำนวนแถวในประวัติ',
  coal_tile_baselines: 'บัญชีในบันทึกตั้งต้น',
  coal_tile_late: 'เก็บย้อนหลัง',
  coal_tile_late_hint: 'บัญชีที่ตอนเริ่มระบบพบว่าไม่มีประวัติเลย คือถูกเขียนโดยสิ่งอื่นที่ไม่ใช่ตัวบันทึกผังบัญชี',
  coal_tile_replay: 'ไม่ตรงกับประวัติ',
  coal_tile_replay_hint: 'บัญชีที่เมื่อเล่นประวัติซ้ำแล้วไม่ได้สภาพปัจจุบัน คือถูกเปลี่ยนนอกตัวบันทึก ตรวจเมื่อสักครู่จาก {{n}} บัญชี',
  coal_untracked: 'คอลัมน์ที่ประวัติไม่ได้ติดตาม: {{cols}}',
  coal_mismatch_lead: 'บัญชีแรก ๆ ที่ไม่ตรง:',
  coal_why_no_history: 'ไม่มีประวัติ',
  coal_why_removed: 'ในประวัติถูกลบแล้ว แต่ยังอยู่ในผังบัญชี',
  coal_why_differs: 'ต่างกันที่ {{cols}}',
  coal_why_gap: 'ถูกเปลี่ยนนอกประวัติระหว่างสองรายการที่บันทึกไว้ ที่ {{cols}}',
  coal_why_missing: 'ยังอยู่ในประวัติ แต่หายไปจากผังบัญชีโดยไม่มีบันทึกการลบ',
  coal_why_recreated: 'ถูกลบนอกประวัติ แล้วถูกสร้างขึ้นใหม่ด้วย id เดิม',
  coal_filter_client: 'ลูกค้า',
  coal_all_clients: 'ลูกค้าทุกราย',
  coal_filter_scope: 'ขอบเขต',
  coal_all_scopes: 'ทุกขอบเขต',
  coal_scope_client_chart: 'ผังบัญชีของลูกค้า',
  coal_scope_house_template: 'แม่แบบที่ใช้อยู่',
  coal_scope_master_firm_layer: 'ชั้นความเห็นของสำนักงานบนแม่แบบหลัก',
  coal_scope_asset_category: 'หมวดสินทรัพย์',
  coal_house: 'แม่แบบที่ใช้อยู่',
  coal_col_when: 'เมื่อ',
  coal_col_client: 'ลูกค้า',
  coal_col_account: 'บัญชี',
  coal_col_change: 'การเปลี่ยนแปลง',
  coal_col_who: 'ผู้เปลี่ยน',
  coal_col_source: 'ผ่านทาง',
  coal_rows_one: '{{count}} แถว',
  coal_rows_other: '{{count}} แถว',
  coal_none: 'ยังไม่มีการบันทึก',
  coal_load_failed: 'โหลดประวัติการเปลี่ยนแปลงไม่ได้: {{error}}',
};
